import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import { previewInstructions, type PersonaDraft } from '../../services/playbookPersonaApi';

const COPY = {
  title: 'What the AI customer is told at the start',
  note:
    'The concerns are not in here. They reach the customer only once the conversation '
    + 'gets to positioning, so it cannot raise them in the opening.',
  loading: 'Building the preview…',
  failed: 'We could not build the preview. Close this and try again.',
  close: 'Close',
} as const;

interface InstructionsPreviewDialogProps {
  open: boolean;
  persona: PersonaDraft | null;
  /** The track's difficulty: it sets how hard the customer is to convince. */
  difficulty?: string;
  onClose: () => void;
}

/**
 * Read-only view of the startup instructions the AI customer gets, built by
 * the backend from the persona as it stands (POST /training/playbook/persona/preview).
 * Fetched fresh each time it opens, so it always reflects the latest edits.
 * A modal, so the text is visible wherever the admin has scrolled.
 */
export const InstructionsPreviewDialog: React.FC<InstructionsPreviewDialogProps> = ({
  open,
  persona,
  difficulty,
  onClose,
}) => {
  const [instructions, setInstructions] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !persona) return undefined;
    let isCancelled = false;
    setInstructions(null);
    setError(null);
    previewInstructions(persona, difficulty)
      .then((result) => {
        if (!isCancelled) setInstructions(result?.instructions || '');
      })
      .catch((previewError: any) => {
        console.error('Error building the instructions preview:', previewError);
        if (!isCancelled) setError(previewError?.message || COPY.failed);
      });
    return () => {
      isCancelled = true;
    };
  }, [open, persona, difficulty]);

  const isLoading = open && instructions === null && !error;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="instructions-preview-title">
      <DialogTitle id="instructions-preview-title">{COPY.title}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {COPY.note}
        </Typography>
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }} role="status">
            <CircularProgress size={18} />
            <Typography variant="body2">{COPY.loading}</Typography>
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {instructions !== null && (
          <Box
            component="pre"
            data-testid="instructions-preview-text"
            sx={{ whiteSpace: 'pre-wrap', fontSize: 12, m: 0, fontFamily: 'monospace', bgcolor: 'grey.50', p: 2, borderRadius: 1 }}
          >
            {instructions}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{COPY.close}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstructionsPreviewDialog;
